// src/components/LandingPage.js
import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, Users, Video, Star } from 'lucide-react';

const LandingContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
`;

const AppLogo = styled.div`
  width: 120px;
  height: 120px;
  background: #1a1a1a;
  border-radius: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    width: 60px;
    height: 60px;
    background: white;
    clip-path: polygon(0% 0%, 100% 50%, 0% 100%);
  }
`;

const AppTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 10px;
  font-weight: 300;
`;

const AppSubtitle = styled.p`
  font-size: 1.2rem;
  margin-bottom: 30px;
  opacity: 0.9;
`;

const StatsContainer = styled.div`
  display: flex;
  gap: 40px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: bold;
`;

const StatLabel = styled.div`
  font-size: 0.9rem;
  opacity: 0.8;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    max-width: 300px;
  }
`;

const ActionButton = styled.button`
  padding: 15px 30px;
  font-size: 1.1rem;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &.primary {
    background: #4CAF50;
    color: white;
    
    &:hover {
      background: #45a049;
      transform: translateY(-2px);
    }
  }
  
  &.secondary {
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
    }
  }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  max-width: 800px;
  margin-top: 40px;
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: 20px;
  border-radius: 12px;
  backdrop-filter: blur(10px);
`;

const LandingPage = () => {
  const navigate = useNavigate();
  const { setUserRole } = useAuth();

  const handleTeacherStart = () => {
    setUserRole('teacher');
    navigate('/setup');
  };

  const handleStudentJoin = () => {
    setUserRole('student');
    navigate('/student/join');
  };

  return (
    <LandingContainer>
      <AppLogo />
      <AppTitle>ClassIn</AppTitle>
      <AppSubtitle>A truly professional online classroom</AppSubtitle>
      
      <StatsContainer>
        <StatItem>
          <StatNumber>4.2 ★</StatNumber>
          <StatLabel>1K reviews</StatLabel>
        </StatItem>
        <StatItem>
          <StatNumber>100K+</StatNumber>
          <StatLabel>Downloads</StatLabel>
        </StatItem>
        <StatItem>
          <StatNumber>PEGI 3</StatNumber>
          <StatLabel>Rating</StatLabel>
        </StatItem>
      </StatsContainer>
      
      <ButtonContainer>
        <ActionButton className="primary" onClick={handleTeacherStart}>
          Start Teaching
        </ActionButton>
        <ActionButton className="secondary" onClick={handleStudentJoin}>
          Join Class
        </ActionButton>
      </ButtonContainer>
      
      <FeatureGrid>
        <FeatureCard>
          <BookOpen size={32} style={{ marginBottom: '10px' }} />
          <h3>Interactive Whiteboard</h3>
          <p>Draw, annotate, and collaborate in real-time</p>
        </FeatureCard>
        <FeatureCard>
          <Users size={32} style={{ marginBottom: '10px' }} />
          <h3>Student Management</h3>
          <p>Manage permissions and track participation</p>
        </FeatureCard>
        <FeatureCard>
          <Video size={32} style={{ marginBottom: '10px' }} />
          <h3>Multi-Device Support</h3>
          <p>Works on phones, tablets, and large displays</p>
        </FeatureCard>
      </FeatureGrid>
    </LandingContainer>
  );
};

export default LandingPage;